/* Admin (User Management): correction-alert bell / banner / sound per team, what the banner Stop button does, and stopped alerts. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, ChevronDown, ChevronRight } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Switch } from "@/components/ui/switch";
import { reloadCorrectionAlerts } from "@/components/CorrectionAlerts";

const LABEL = { admins: "Admins", Account: "Account Team", Processing: "Processing Team", BackOffice: "Back Office Team", ITDept: "IT Department", Partners: "Partners Team" };
const HOURS = [1, 2, 4, 8, 12, 24, 48];

export const CorrectionAlertSettingsCard = () => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const load = useCallback(async () => {
    try { setS((await axios.get(`${API}/order-register/corrections/alert-settings`, { headers: getAuthHeader() })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load alert settings"); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const save = async (body) => {
    try {
      const r = (await axios.put(`${API}/order-register/corrections/alert-settings`, body, { headers: getAuthHeader() })).data;
      setS((x) => ({ ...x, ...r })); toast.success("Alert settings saved"); reloadCorrectionAlerts();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const resume = async (inv) => {
    try { await axios.delete(`${API}/order-register/corrections/alerts/${inv}/stop`, { headers: getAuthHeader() }); toast.success(`${inv} alert back on`); load(); reloadCorrectionAlerts(); }
    catch { toast.error("Could not resume"); }
  };
  const pill = (on, label, onClick, tid) => (
    <button type="button" onClick={onClick} data-testid={tid}
      className={`rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-red-600 bg-red-600 text-white" : "hover:bg-slate-100"}`}>{label}</button>
  );
  return (
    <div className="rounded-lg border border-red-200 bg-white p-3" data-testid="corr-alert-settings-card">
      <button type="button" className="flex w-full items-center gap-2 text-left text-sm font-semibold text-red-900" onClick={() => setOpen((v) => !v)} data-testid="corr-alert-settings-toggle">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<BellRing className="h-4 w-4" /> Correction alerts — notifications &amp; bell settings</button>
      {open && s && (
        <div className="mt-2 space-y-3 text-xs">
          <p className="text-slate-500">Rings when a changed completed invoice has no corrected packing slip after 24 h (admins + the person who made the change).</p>
          <table className="w-full" data-testid="corr-alert-teams">
            <thead><tr className="border-b text-left text-slate-500"><th className="py-1">Team</th><th>Red bell</th><th>Banner</th><th>Sound</th></tr></thead>
            <tbody>{Object.entries(s.teams).map(([t, p]) => (
              <tr key={t} className="border-b last:border-0">
                <td className="py-1">{LABEL[t] || t}</td>
                {["bell", "banner", "sound"].map((k) => (
                  <td key={k}><Switch checked={p[k]} onCheckedChange={(v) => save({ teams: { [t]: { ...p, [k]: v } } })} data-testid={`corr-alert-${t}-${k}`} /></td>))}
              </tr>))}</tbody>
          </table>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">Banner "Stop" default:</span>
            {pill(s.stop_mode === "temporary", "Temporary", () => save({ stop_mode: "temporary" }), "corr-alert-mode-temporary")}
            {pill(s.stop_mode === "permanent", "Permanent", () => save({ stop_mode: "permanent" }), "corr-alert-mode-permanent")}
            <span className="ml-3 font-semibold">Temporary stop for:</span>
            <select value={s.snooze_hours} onChange={(e) => save({ snooze_hours: Number(e.target.value) })} className="h-7 rounded border px-1" data-testid="corr-alert-snooze">
              {HOURS.map((h) => <option key={h} value={h}>{h} h</option>)}</select>
          </div>
          <div data-testid="corr-alert-muted">
            <p className="mb-1 font-semibold">Stopped alerts ({s.muted.length})</p>
            {s.muted.map((m) => (
              <div key={m.invoice_key} className="flex items-center gap-2 border-b py-1 last:border-0" data-testid={`corr-alert-muted-${m.invoice_key}`}>
                <b className="font-mono">{m.invoice_key}</b>
                <span className="text-slate-500">{m.mute.mode === "permanent" ? "Permanently" : `Until ${new Date(m.mute.until).toLocaleString()}`} · by {m.mute.by_name}</span>
                <button type="button" onClick={() => resume(m.invoice_key)} className="ml-auto text-red-700 underline" data-testid={`corr-alert-resume-${m.invoice_key}`}>Turn back on</button>
              </div>))}
            {!s.muted.length && <p className="text-slate-400">None</p>}
          </div>
        </div>)}
    </div>
  );
};
